
import {
    AddDataSharedFunc, DashboardSharedFilterFunc, DashboardSharedFilterKey,
    EcogGroup,
    HHSOfResidence,
    IDashboardSharedChartData, IDashboardSharedDimensionsViewModel, Remoteness, TnmStage,
} from "models/common";
import { IFilters, IFilterKeys, DimensionsById, ISourceData, IDimension, IDimMDT, IDimFacility } from "models/core";

//Plots for this dashboard
import * as ReviewsByPeriod from "./reviewByPeriodHierarchy";
import * as PrimarySite from "./primarySiteHierarchy";
import * as PrimarySiteSummary from "./primarySiteSummary";
import * as Morphology from "./morphology";
import { IDashboardViewModel } from "./viewModel";

export { type IDashboardDataQualityViewModel, type IDashboardViewModel } from "./viewModel";

export interface IDashboardSource {
    eligible: IDashboardViewModel[];
    ineligible: IDashboardViewModel[];
}

export interface IDashboardDimensionsViewModel extends IDashboardSharedDimensionsViewModel {
    hhsResidenceGroups?: DimensionsById;
    dataQualityIndicators?: DimensionsById;
    monthsArray?: Array<IDimension>;
    yearsArray?: Array<IDimension>;
    months?: { [monthId: number]: string };
    years?: { [yearId: number]: string };
    dqCompFacility?: { [facilityYearKey: string]: number };
    dqCompState?: { [facilityYearKey: string]: number };
    oneYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    threeYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    fiveYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    tenYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    kmSurvivalMDT?: {[mdtCancerKey: string]: { survivalTime: string, mdtKMSurvival: number, stateKMSurvival: number }};

}

interface ILevel1ChartData {
    totalDiagnoses: {colon: number, rectal: number};
	totalDataQualityEligibleDiagnoses: number;
	facilities: IDimFacility[];
	hhses: IDimension[];
	mdts: IDimMDT[];
	month: IDimension[];
	year: IDimension[];

    diagToSurg30Days: { colon:{ numerator, denominator: number, percentage: number }, rectal: { numerator, denominator: number, percentage: number } };
    diagToFirstTreatment30Days: { colon:{ numerator, denominator: number, percentage: number }, rectal: { numerator, denominator: number, percentage: number } };
    diagToFirstST42Days: { colon:{ numerator, denominator: number, percentage: number }, rectal: { numerator, denominator: number, percentage: number } };
    lengthOfStay: { colon: {lowerquart: number, median: number, upperquart: number }, rectal: {lowerquart: number, median: number, upperquart: number }} ;
    prolongedLOS: { colon:{ numerator, denominator: number, percentage: number }, rectal: { numerator, denominator: number, percentage: number } };
    hadAnastomicLeak: { colon:{ numerator, denominator: number, percentage: number }, rectal: { numerator, denominator: number, percentage: number } };
    hadAnastomicLeakDied90Days:{ colon:{ numerator, denominator: number, percentage: number }, rectal: { numerator, denominator: number, percentage: number } };
    lapConvertedOpenSurg: { colon:{ numerator, denominator: number, percentage: number }, rectal: { numerator, denominator: number, percentage: number } };
    hadStomaAtSurg: { colon:{ numerator, denominator: number, percentage: number }, rectal: { numerator, denominator: number, percentage: number } };
    hadAbdominoperinealResection: { colon:{ numerator, denominator: number, percentage: number }, rectal: { numerator, denominator: number, percentage: number } };
    inpatientDeath: { colon:{ numerator, denominator: number, percentage: number }, rectal: { numerator, denominator: number, percentage: number } };
    death30Days: { colon:{ numerator, denominator: number, percentage: number }, rectal: { numerator, denominator: number, percentage: number } };
    death90Days: { colon:{ numerator, denominator: number, percentage: number }, rectal: { numerator, denominator: number, percentage: number } };
    stage3Adjuvant: { colon:{ numerator, denominator: number, percentage: number }, rectal: { numerator, denominator: number, percentage: number } };
    neoAdjuvantXRT: { colon:{ numerator, denominator: number, percentage: number }, rectal: { numerator, denominator: number, percentage: number } };
    survival365Days: { colon:{ numerator, denominator: number, percentage: number }, rectal: { numerator, denominator: number, percentage: number } };
    survival730Days: { colon:{ numerator, denominator: number, percentage: number }, rectal: { numerator, denominator: number, percentage: number } };
    survival1825Days: { colon:{ numerator, denominator: number, percentage: number }, rectal: { numerator, denominator: number, percentage: number } };
	// Export
	filteredReferralIdCsv: string;
}

// HACK: chop out the reference array from the shared interface 
type RestrictedSharedChartData = Omit<Omit<Omit<Omit<IDashboardSharedChartData, "facilities">, "hhses">, "tnmStage">, "ecogGroup">;

interface ILevel2ChartData extends RestrictedSharedChartData {
	totalDiagnoses: number;
	primarySite: PrimarySite.IHierarchyPlotData;
	morphology: Morphology.IPlotData;
	remoteness: Remoteness.IPlotData;
	hhsOfResidence: HHSOfResidence.IPlotData;
    tnmStage: {colon: TnmStage.IPlotData, rectal: TnmStage.IPlotData};
    ecogGroup: {colon: EcogGroup.IPlotData, rectal: EcogGroup.IPlotData};
}

export interface IChartData {
	level1: ILevel1ChartData;
	level2: ILevel2ChartData;
}

const FilterKey: IFilterKeys = {
    HHSResidenceGroup: { key: "HHSResidenceGroup", label: "HHS residence group" },
    HHSOfResidence: { key: "HHSOfResidence", label: "HHS of residence" },
    DataQualityEligible: { key: "DataQualityEligible", label: "Data quality eligible" },
    PrimarySite: { key: "PrimarySite", label: "Primary site" },
    Morphology: { key: "Morphology", label: "Morphology" },
    Remoteness: { key: "Remoteness", label: "Rurality" },
    FirstTreatment: { key: "FirstTreatment", label: "First Treatment" },
    TimeToFirstTreatment: { key: "TimeToFirstTreatment", label: "Time To First Treatment" },
    DiagMonth: { key: "DiagMonth", label: "Diagnosis Month" },
    DiagYear: { key: "DiagYear", label: "Diagnosis Year" },
    MdtMonth: { key: "MdtMonth", label: "MDT Month" },
    // DaysFromDiagToSurg: { key: "DaysFromDiagToSurg", label: "Days From Diagnosis To Surgery <= 30 Days" },
    // InHospitalMortality: { key: "InHospitalMortality", label: "In Hospital Mortality" },
    // ThirtyDayMortality: { key: "ThirtyDayMortality", label: "30 Day Mortality" },
    // NinetyDayMortality: { key: "NinetyDayMortality", label: "90 Day Mortality" },
    // OneYearSurvival: { key: "OneYearSurvival", label: "1 Year Survival" },
    // TwoYearSurvival: { key: "TwoYearSurvival", label: "2 Year Survival" },
    // Stage3AdjuvantTx: { key: "Stage3AdjuvantTx", label: "Stage 3 Adjuvant Therapy" },
    // NeoAdjuvantXRT: { key: "NeoAdjuvantXRT", label: "Neo Adjuvant XRT" },
}

export const DashboardFilterKeys: IFilterKeys = {
    ...DashboardSharedFilterKey,
    ...FilterKey
}

export type Filters = IFilters<typeof DashboardFilterKeys>;
export type DashboardFilterFunc = DashboardSharedFilterFunc<IDashboardViewModel>;
export type AddDataFunc = AddDataSharedFunc<IDashboardViewModel, ISourceData>;

